import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import { useNavigate, useParams } from "react-router-dom";

import api from "../services/api/axios";

export default function ResetPassword({ tokenOverride = null }) {
  const navigate = useNavigate();
  const { token: routeToken } = useParams();
  const token = tokenOverride || routeToken;
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const passwordError = useMemo(() => {
    if (!newPassword) return "";
    if (newPassword.length < 8) return "Password must be at least 8 characters long.";
    return "";
  }, [newPassword]);

  const confirmError = useMemo(() => {
    if (!confirmPassword) return "";
    if (newPassword !== confirmPassword) return "Passwords do not match.";
    return "";
  }, [confirmPassword, newPassword]);

  useEffect(() => {
    if (!success) return undefined;
    const timeout = window.setTimeout(() => navigate("/login", { replace: true }), 2500);
    return () => window.clearTimeout(timeout);
  }, [navigate, success]);

  const handleSubmit = async () => {
    if (!token) {
      setError("Invalid or expired reset token.");
      return;
    }
    if (!newPassword || !confirmPassword) {
      setError("Please enter and confirm your new password.");
      return;
    }
    if (passwordError || confirmError) {
      setError(passwordError || confirmError);
      return;
    }

    setLoading(true);
    setError("");

    try {
      await api.post("/auth/reset-password", {
        token,
        new_password: newPassword,
      });
      setSuccess(true);
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Unable to reset password.";
      setError(typeof msg === "string" ? msg : "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (event) => {
    if (event.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        background: "linear-gradient(135deg, rgb(16,105,13), rgb(25,120,199))",
      }}
    >
      <Paper
        elevation={10}
        sx={{
          width: "100%",
          maxWidth: 680,
          p: { xs: 4, sm: 6, md: 7 },
          borderRadius: 4,
          backgroundColor: "#ffffffee",
          backdropFilter: "blur(8px)",
          border: "3px solid #00336C",
        }}
      >
        <Typography
          component="div"
          aria-label="ProspectEngage CRM"
          sx={{
            textAlign: "center",
            fontSize: { xs: 34, sm: 44 },
            fontWeight: 900,
            lineHeight: 1,
            mb: 4,
          }}
        >
          <Box component="span" sx={{ color: "#00336C" }}>
            Prospect
          </Box>
          <Box component="span" sx={{ color: "#648315" }}>
            Engage
          </Box>
        </Typography>

        <Typography variant="h4" sx={{ color: "#00336C", fontWeight: 800, mb: 1.5 }}>
          Reset Password
        </Typography>
        <Typography sx={{ color: "#425466", mb: 4, lineHeight: 1.6, fontSize: 18 }}>
          Choose a new password for your BusinessEngage account.
        </Typography>

        <TextField
          label="New Password"
          type="password"
          fullWidth
          margin="normal"
          value={newPassword}
          onChange={(event) => setNewPassword(event.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="new-password"
          error={Boolean(passwordError)}
          helperText={passwordError || "Use at least 8 characters."}
        />

        <TextField
          label="Confirm Password"
          type="password"
          fullWidth
          margin="normal"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
          onKeyDown={onKeyDown}
          autoComplete="new-password"
          error={Boolean(confirmError)}
          helperText={confirmError || " "}
        />

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            Password reset successfully. Redirecting to login.
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          startIcon={loading ? null : <LockResetIcon />}
          fullWidth
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            mt: 4,
            py: 1.65,
            borderRadius: 3,
            backgroundColor: "#00336C",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { backgroundColor: "#115293" },
          }}
        >
          {loading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
        </Button>

        <Box sx={{ mt: 2, textAlign: "center" }}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => navigate("/login")}
            sx={{ color: "#00336C", fontWeight: 700 }}
          >
            Back to Login
          </Link>
        </Box>
      </Paper>
    </Box>
  );
}
